import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserPanelPages.css";
import Navbar from "../Component/Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

/* ==============================
   FORMAT HELPERS
============================== */
const formatAmount = (value = 0) => {
  const amount = Number(value || 0);
  return `₹${amount.toLocaleString("en-IN")}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "-";
  }
};

const normalizeOrder = (item = {}, index = 0) => ({
  id: item.id || item.orderId || item.order_id || `order-${index}`,
  orderNo:
    item.orderNo ||
    item.order_no ||
    item.orderNumber ||
    item.order_number ||
    `ORD-${item.id || index + 1}`,
  productName:
    item.productName ||
    item.product_name ||
    item.planName ||
    item.plan_name ||
    item.title ||
    "Order",
  amount: item.rawAmount ?? item.raw_amount ?? item.amount ?? 0,
  amountText:
    typeof item.amount === "string" && item.amount.startsWith("₹")
      ? item.amount
      : formatAmount(item.rawAmount ?? item.raw_amount ?? item.amount ?? 0),
  status: String(item.status || "pending").toLowerCase(),
  paymentId: item.paymentId || item.payment_id || "",
  createdAt: item.createdAt || item.created_at || "",
  createdAtText:
    item.createdAtFormatted ||
    item.created_at_formatted ||
    formatDateTime(item.createdAt || item.created_at),
});

const getStatusLabel = (status = "") => {
  const clean = String(status || "").toLowerCase();

  if (clean === "success" || clean === "completed") return "Completed";
  if (clean === "pending") return "Pending";
  if (clean === "failed") return "Failed";
  if (clean === "cancelled" || clean === "canceled") return "Cancelled";

  return clean || "Pending";
};

function OrdersPage() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const redirectToSignIn = useCallback(() => {
    clearAuthStorage();
    navigate("/signin", { replace: true });
  }, [navigate]);

  /* ==============================
     PROTECTED FETCH
  ============================== */
  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const token = getToken();

      if (!token) {
        redirectToSignIn();
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        redirectToSignIn();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [redirectToSignIn]
  );

  /* ==============================
     LOAD ORDERS
  ============================== */
  const fetchOrders = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setStatus({ type: "", message: "" });

        const data = await authFetch("/orders?page=1&limit=20");

        const safeOrders = Array.isArray(data?.data)
          ? data.data.map(normalizeOrder)
          : Array.isArray(data?.orders)
          ? data.orders.map(normalizeOrder)
          : [];

        setOrders(safeOrders);

        setPagination({
          page: Number(data?.pagination?.page || 1),
          limit: Number(data?.pagination?.limit || 20),
          total: Number(data?.pagination?.total || safeOrders.length),
          totalPages: Number(data?.pagination?.totalPages || 1),
        });
      } catch (err) {
        setOrders([]);

        setPagination({
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
        });

        setStatus({
          type: "error",
          message: err.message || "Failed to fetch orders.",
        });
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        fetchOrders(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleOrderUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      setStatus({
        type: "success",
        message: payload?.message || "Orders updated successfully.",
      });

      softRefresh();
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        setStatus({
          type: "success",
          message: payload.message || "Payment completed successfully.",
        });
      }

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      redirectToSignIn();
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("order:new", handleOrderUpdate);
    socket.on("order:update", handleOrderUpdate);
    socket.on("order:status", handleOrderUpdate);

    socket.on("payment:status", handlePaymentStatus);
    socket.on("domain:status", handleRealtimeRefresh);
    socket.on("hosting:status", handleRealtimeRefresh);
    socket.on("dashboard:update", handleRealtimeRefresh);
    socket.on("notification:new", handleRealtimeRefresh);
    socket.on("activity:new", handleRealtimeRefresh);
    socket.on("wallet:update", handleRealtimeRefresh);
    socket.on("wallet:status", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("order:new", handleOrderUpdate);
      socket.off("order:update", handleOrderUpdate);
      socket.off("order:status", handleOrderUpdate);

      socket.off("payment:status", handlePaymentStatus);
      socket.off("domain:status", handleRealtimeRefresh);
      socket.off("hosting:status", handleRealtimeRefresh);
      socket.off("dashboard:update", handleRealtimeRefresh);
      socket.off("notification:new", handleRealtimeRefresh);
      socket.off("activity:new", handleRealtimeRefresh);
      socket.off("wallet:update", handleRealtimeRefresh);
      socket.off("wallet:status", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [fetchOrders, redirectToSignIn]);

  const handleRefresh = () => {
    fetchOrders(true);
  };

  return (
    <>
      <Navbar />

      <section className="user-page-section">
        <div className="user-page-container">
          <div className="user-page-head">
            <div>
              <h1>My Orders</h1>

              <p>Track your purchases and invoices securely.</p>

              <div className="user-realtime-row">
                <div className="user-realtime-chip">
                  <span
                    className={
                      realtimeStatus === "connected"
                        ? "user-realtime-dot online"
                        : "user-realtime-dot offline"
                    }
                  />

                  <span>
                    {realtimeStatus === "connected"
                      ? "Realtime connected"
                      : realtimeStatus === "connecting"
                      ? "Connecting realtime..."
                      : "Realtime offline"}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="user-page-refresh-btn"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {status.message ? (
            <div className={`user-page-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="user-page-card">
            {loading ? (
              <p className="user-page-muted">Loading orders...</p>
            ) : orders.length === 0 ? (
              <p className="user-page-muted">No orders found yet.</p>
            ) : (
              <>
                <div className="user-table-wrap">
                  <table className="user-table">
                    <thead>
                      <tr>
                        <th>Order No</th>
                        <th>Product</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Payment ID</th>
                        <th>Date</th>
                      </tr>
                    </thead>

                    <tbody>
                      {orders.map((item) => (
                        <tr key={item.id || item.orderNo}>
                          <td>{item.orderNo}</td>

                          <td>{item.productName}</td>

                          <td>{item.amountText}</td>

                          <td>
                            <span
                              className={`status-pill ${String(
                                item.status || ""
                              ).toLowerCase()}`}
                            >
                              {getStatusLabel(item.status)}
                            </span>
                          </td>

                          <td>{item.paymentId || "-"}</td>

                          <td>{item.createdAtText}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="user-page-muted">
                  Showing {orders.length} of {pagination.total} orders.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default OrdersPage;